import type { SavedViewRule } from '@portfolio-pm/shared';

export const DEFAULT_RULE_TREE: SavedViewRule = {
  type: 'group',
  operator: 'all',
  children: [{ type: 'condition', field: 'completed', operator: 'is', value: 'false' }],
};

export function ruleTreeToQuery(rule: SavedViewRule): string {
  if (rule.type === 'condition') return conditionToQuery(rule);
  const children = rule.children.map(ruleTreeToQuery).filter(Boolean);
  if (children.length === 0) return 'incomplete';
  const joiner = rule.operator === 'any' ? ' OR ' : ' AND ';
  const joined = children.map((child) => (child.includes(' ') ? `(${child})` : child)).join(joiner);
  return rule.operator === 'none' ? `NOT (${joined})` : joined;
}

function conditionToQuery(rule: Extract<SavedViewRule, { type: 'condition' }>): string {
  if (rule.field === 'raw') return rule.value ?? '';
  const value = rule.value?.trim();

  if (rule.operator === 'missing') return `NOT ${existsAtom(rule.field)}`;
  if (rule.operator === 'exists') return existsAtom(rule.field);

  if (rule.field === 'completed') {
    const completed = value === 'true' || value === 'completed';
    return rule.operator === 'is_not'
      ? completed
        ? 'incomplete'
        : 'completed'
      : completed
        ? 'completed'
        : 'incomplete';
  }
  if (rule.field === 'trashed') {
    const trashed = value === 'true' || value === 'trashed';
    return rule.operator === 'is_not'
      ? trashed
        ? 'NOT trashed'
        : 'trashed'
      : trashed
        ? 'trashed'
        : 'NOT trashed';
  }
  if (rule.field === 'recurrence') return rule.operator === 'is_not' ? 'NOT repeat' : 'repeat';
  if (
    [
      'child',
      'root',
      'haschildren',
      'hassubtasks',
      'issubtask',
      'url',
      'hasurl',
      'hasnotes',
      'favorite',
    ].includes(rule.field) &&
    !value
  ) {
    return rule.operator === 'is_not' ? `NOT ${rule.field}` : rule.field;
  }

  const atom = atomForCondition(rule.field, rule.operator, value);
  return rule.operator === 'is_not' ? `NOT ${atom}` : atom;
}

function atomForCondition(
  field: Extract<SavedViewRule, { type: 'condition' }>['field'],
  operator: Extract<SavedViewRule, { type: 'condition' }>['operator'],
  value: string | undefined,
): string {
  const escaped = value ? value.replace(/\s+/g, '_') : '';
  if (field === 'due') {
    if (operator === 'before') return `dueBefore:${escaped}`;
    if (operator === 'after') return `dueAfter:${escaped}`;
    return `due:${escaped}`;
  }
  if (field === 'scheduled') {
    if (operator === 'before') return `scheduledBefore:${escaped}`;
    if (operator === 'after') return `scheduledAfter:${escaped}`;
    return `scheduled:${escaped}`;
  }
  if (field === 'text' || field === 'title') return `${field}:${escaped}`;
  return value ? `${field}:${escaped}` : field;
}

function existsAtom(field: Extract<SavedViewRule, { type: 'condition' }>['field']): string {
  if (field === 'url') return 'url';
  if (field === 'hasurl') return 'hasurl';
  if (field === 'hasnotes') return 'hasnotes';
  if (field === 'hassubtasks') return 'hassubtasks';
  if (field === 'issubtask') return 'issubtask';
  if (field === 'favorite') return 'favorite';
  if (field === 'recurrence') return 'repeat';
  if (field === 'trashed') return 'trashed';
  if (field === 'completed') return 'completed';
  return `${field}:*`;
}
